import { useState, useCallback, useMemo, useDeferredValue } from "react";
import {
  useGetApiDevices,
  useGetApiBicsEndpoints,
  useGetApiCustomers,
  useGetApiProjects,
  useGetApiDeployments,
  usePostApiDevicesIdSync,
} from "@charphq/api-client";

interface Device {
  id: string;
  name: string;
  devEUI: string;
  externalId?: string | null;
  deviceType?: {
    id: string;
    name: string;
    description?: string;
    category?: string;
  };
  status?: string;
  lastSeenAt?: string;
  externalTags?: Record<string, any> | string[] | null;
  externalConfig?: Record<string, any>;
  serialNumber?: string;
  model?: string;
  manufacturer?: string;
  currentCustomer?: { id: string; name: string } | null;
  currentProject?: { id: string; name: string } | null;
  currentDeployment?: { id: string; name: string } | null;
}

/** A BICS endpoint, trimmed to what the search result card shows. */
interface SimCard {
  id: string;
  iccid: string;
  /** The BICS endpoint name, e.g. `Maxi_A3F1B2`. */
  name?: string;
  /** The BICS lifecycle code (`A`, `S`, …). */
  status?: string;
  device?: {
    id: string;
    name: string;
  };
}

const asString = (value: unknown): string | undefined =>
  typeof value === "string" && value !== "" ? value : undefined;

function toSimCard(endpoint: Record<string, unknown>): SimCard {
  const deviceId = asString(endpoint.deviceId);
  return {
    // Keys the result card, so never collapse a numeric id to "".
    id: typeof endpoint.id === "number" ? String(endpoint.id) : (asString(endpoint.id) ?? ""),
    iccid: asString(endpoint.iccid) ?? "",
    name: asString(endpoint.name),
    status: asString(endpoint.status),
    device: deviceId
      ? { id: deviceId, name: asString(endpoint.deviceName) ?? deviceId }
      : undefined,
  };
}

interface Customer {
  id: string;
  name: string;
  email?: string | null;
  phone?: string | null;
  isActive?: boolean;
}

interface Project {
  id: string;
  name: string;
  description?: string | null;
  status?: string;
}

interface Deployment {
  id: string;
  name: string;
  description?: string | null;
  status?: string;
  monitoringEnabled?: boolean;
}

interface SearchState {
  query: string;
  devices: { data: Device[]; isLoading: boolean; error?: Error };
  customers: { data: Customer[]; isLoading: boolean; error?: Error };
  projects: { data: Project[]; isLoading: boolean; error?: Error };
  deployments: { data: Deployment[]; isLoading: boolean; error?: Error };
  simCards: { data: SimCard[]; isLoading: boolean; error?: Error };
}

interface UseUniversalSearchResult {
  searchState: SearchState;
  setQuery: (query: string) => void;
  clearSearch: () => void;
  hasResults: boolean;
  totalResults: number;
  syncDevice: (deviceId: string) => Promise<void>;
  refetchDevices: () => Promise<void>;
}

export function useUniversalSearch(): UseUniversalSearchResult {
  const [query, setQueryState] = useState("");

  // Debounced query for API calls (300ms delay)
  const debouncedQuery = useDeferredValue(query);

  // Normalize query to ensure consistent query keys (empty strings become undefined)
  const normalizedQuery = debouncedQuery.trim() || undefined;

  // Use the generated API hooks instead of direct fetch calls
  const {
    data: devicesResponse,
    isLoading: devicesLoading,
    error: devicesError,
    refetch: refetchDevices,
  } = useGetApiDevices(
    {
      q: normalizedQuery,
      limit: 20,
      sync: normalizedQuery && normalizedQuery.length >= 2 ? true : undefined,
    },
    {
      query: {
        enabled: normalizedQuery !== undefined && normalizedQuery.length >= 2,
        staleTime: 0, // Always refetch to ensure fresh data
        refetchOnWindowFocus: false, // Don't refetch on focus to avoid unnecessary calls
        refetchOnMount: true,
      },
    },
  );

  // SIM cards are searched in BICS, their system of record (ADR 0012), not the
  // local sim_cards table — that only holds device links, so every card not yet
  // assigned to a device was unfindable (OTO-155).
  const {
    data: simCardsResponse,
    isLoading: simCardsLoading,
    error: simCardsError,
  } = useGetApiBicsEndpoints(
    {
      q: normalizedQuery,
      limit: 10,
    },
    {
      query: {
        enabled: normalizedQuery !== undefined && normalizedQuery.length >= 2,
        staleTime: 0, // Always refetch to ensure fresh data
        refetchOnWindowFocus: false, // Don't refetch on focus to avoid unnecessary calls
        refetchOnMount: true,
      },
    },
  );

  // Shared query options for the non-device entities (no sync; device-only).
  const entityQueryOptions = {
    enabled: normalizedQuery !== undefined && normalizedQuery.length >= 2,
    staleTime: 0,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  } as const;

  const {
    data: customersResponse,
    isLoading: customersLoading,
    error: customersError,
  } = useGetApiCustomers({ q: normalizedQuery, limit: 5 }, { query: entityQueryOptions });

  const {
    data: projectsResponse,
    isLoading: projectsLoading,
    error: projectsError,
  } = useGetApiProjects({ q: normalizedQuery, limit: 5 }, { query: entityQueryOptions });

  const {
    data: deploymentsResponse,
    isLoading: deploymentsLoading,
    error: deploymentsError,
  } = useGetApiDeployments({ q: normalizedQuery, limit: 5 }, { query: entityQueryOptions });

  // React Query will automatically refetch when normalizedQuery changes (query key changes)
  // With staleTime: 0, data is always considered stale and will refetch automatically

  // Device sync mutation
  const syncDeviceMutation = usePostApiDevicesIdSync();

  const setQuery = useCallback((newQuery: string) => {
    setQueryState(newQuery);
  }, []);

  const clearSearch = useCallback(() => {
    setQueryState("");
    // Queries will automatically be disabled when query is cleared due to enabled condition
  }, []);

  const syncDevice = useCallback(
    async (deviceId: string) => {
      try {
        await syncDeviceMutation.mutateAsync({
          id: deviceId,
        });
      } catch (error) {
        console.error("Failed to sync device:", error);
        throw error;
      }
    },
    [syncDeviceMutation],
  );

  // Extract data from API responses, computing devEUI from externalId/serialNumber
  const devices = ((devicesResponse as any)?.data || []).map((d: any) => ({
    ...d,
    devEUI: d.externalId || d.serialNumber || "",
  }));
  const simCards: SimCard[] = (simCardsResponse?.data ?? []).map(toSimCard);
  const customers = (customersResponse as any)?.data || [];
  const projects = (projectsResponse as any)?.data || [];
  const deployments = (deploymentsResponse as any)?.data || [];

  const searchState: SearchState = useMemo(
    () => ({
      query,
      devices: {
        data: devices,
        isLoading: devicesLoading,
        error: devicesError as Error | undefined,
      },
      customers: {
        data: customers,
        isLoading: customersLoading,
        error: customersError as Error | undefined,
      },
      projects: {
        data: projects,
        isLoading: projectsLoading,
        error: projectsError as Error | undefined,
      },
      deployments: {
        data: deployments,
        isLoading: deploymentsLoading,
        error: deploymentsError as Error | undefined,
      },
      simCards: {
        data: simCards,
        isLoading: simCardsLoading,
        error: simCardsError as Error | undefined,
      },
    }),
    [
      query,
      devices,
      devicesLoading,
      devicesError,
      customers,
      customersLoading,
      customersError,
      projects,
      projectsLoading,
      projectsError,
      deployments,
      deploymentsLoading,
      deploymentsError,
      simCards,
      simCardsLoading,
      simCardsError,
    ],
  );

  const hasResults = useMemo(() => {
    return (
      devices.length > 0 ||
      customers.length > 0 ||
      projects.length > 0 ||
      deployments.length > 0 ||
      simCards.length > 0
    );
  }, [devices.length, customers.length, projects.length, deployments.length, simCards.length]);

  const totalResults = useMemo(() => {
    return (
      devices.length + customers.length + projects.length + deployments.length + simCards.length
    );
  }, [devices.length, customers.length, projects.length, deployments.length, simCards.length]);

  const refetchDevicesWrapper = useCallback(async () => {
    await refetchDevices();
  }, [refetchDevices]);

  return {
    searchState,
    setQuery,
    clearSearch,
    hasResults,
    totalResults,
    syncDevice,
    refetchDevices: refetchDevicesWrapper,
  };
}
